import type { SupabaseClient } from "@supabase/supabase-js";

export type InvitationRole = "manager" | "operator";

export interface ManagerInvitationRequest {
  operationId: string;
  farmId: string;
  email: string;
  fullName: string;
  role: InvitationRole;
}

export interface ManagerInvitationResult {
  status: "attached" | "invited" | "replayed" | "existing_user_attached";
  operationId: string;
  userId: string;
  email: string;
  role: InvitationRole;
}

export interface ManagerInvitationListItem {
  operationId: string;
  email: string;
  fullName: string;
  role: InvitationRole;
  state: "prepared" | "attached";
  authUserId?: string;
  createdAt: string;
  attachedAt?: string;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function records(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.map(record) : [];
}

export function normalizeInvitationEmail(value: string): string {
  return value.trim().toLowerCase();
}

export function buildManagerInvitationRequest(input: {
  operationId: string;
  farmId: string;
  email: string;
  fullName: string;
  role: InvitationRole;
}): ManagerInvitationRequest {
  const email = normalizeInvitationEmail(input.email);
  const fullName = input.fullName.trim();

  if (!UUID_PATTERN.test(input.operationId)) throw new Error("INVITATION_OPERATION_ID_INVALID");
  if (!UUID_PATTERN.test(input.farmId)) throw new Error("INVITATION_FARM_ID_INVALID");
  if (!EMAIL_PATTERN.test(email) || email.length > 320) throw new Error("INVITATION_EMAIL_INVALID");
  if (fullName.length < 2 || fullName.length > 160) throw new Error("INVITATION_NAME_INVALID");
  if (input.role !== "manager" && input.role !== "operator") {
    throw new Error("INVITATION_ROLE_INVALID");
  }

  return {
    operationId: input.operationId,
    farmId: input.farmId,
    email,
    fullName,
    role: input.role,
  };
}

async function edgeFunctionErrorCode(error: unknown): Promise<string | null> {
  if (!error || typeof error !== "object" || !("context" in error)) return null;

  const context = (error as { context?: unknown }).context;
  if (!(context instanceof Response)) return null;

  try {
    const payload = record(await context.clone().json());
    const errorCode = payload.errorCode;
    return typeof errorCode === "string" && errorCode ? errorCode : null;
  } catch {
    return null;
  }
}

export async function inviteFarmUser(
  client: SupabaseClient,
  input: ManagerInvitationRequest,
): Promise<ManagerInvitationResult> {
  const request = buildManagerInvitationRequest(input);
  const { data, error } = await client.functions.invoke("manager-user-invite", {
    body: request,
  });

  if (error) {
    const errorCode = await edgeFunctionErrorCode(error);
    throw new Error(errorCode ?? error.message);
  }

  const payload = record(data);
  if (
    !["attached", "invited", "replayed", "existing_user_attached"].includes(String(payload.status))
  ) {
    throw new Error(String(payload.errorCode ?? "MANAGER_INVITATION_FAILED"));
  }

  if (
    !UUID_PATTERN.test(String(payload.operationId ?? "")) ||
    !UUID_PATTERN.test(String(payload.userId ?? ""))
  ) {
    throw new Error("MANAGER_INVITATION_RESPONSE_INVALID");
  }

  const role = payload.role;
  if (role !== "manager" && role !== "operator") {
    throw new Error("MANAGER_INVITATION_RESPONSE_INVALID");
  }

  return {
    status: payload.status as ManagerInvitationResult["status"],
    operationId: String(payload.operationId),
    userId: String(payload.userId),
    email: normalizeInvitationEmail(String(payload.email ?? request.email)),
    role,
  };
}

export async function loadManagerInvitations(
  client: SupabaseClient,
  farmId: string,
): Promise<ManagerInvitationListItem[]> {
  const { data, error } = await client.rpc("manager_user_invitation_list_v1", {
    target_farm_id: farmId,
  });
  if (error) throw error;

  return records(data).map((item) => {
    const role = item.requested_role;
    if (role !== "manager" && role !== "operator") {
      throw new Error("INVITATION_ROLE_INVALID");
    }

    const state = item.state === "attached" ? "attached" : "prepared";
    const authUserId =
      typeof item.auth_user_id === "string" && item.auth_user_id ? item.auth_user_id : undefined;
    const attachedAt =
      typeof item.attached_at === "string" && item.attached_at ? item.attached_at : undefined;

    return {
      operationId: String(item.operation_id),
      email: normalizeInvitationEmail(String(item.email_normalized)),
      fullName: String(item.full_name),
      role,
      state,
      ...(authUserId ? { authUserId } : {}),
      createdAt: String(item.created_at),
      ...(attachedAt ? { attachedAt } : {}),
    };
  });
}
